import React, { type ErrorInfo, type ReactNode } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type FireEventType } from '@atlaskit/teams-app-internal-analytics';
import {
	type Analytics,
	AnalyticsEventActions,
	AnalyticsEventSource,
	GenericErrorPage,
	logException,
} from '@atlassian/ptc-common';

interface Props {
	analytics: Analytics;
	fireEvent: FireEventType;
	appName: string;
	children: ReactNode;
}

interface State {
	hasError: boolean;
}

// eslint-disable-next-line @repo/internal/react/no-class-components
export default class ErrorBoundary extends React.Component<Props, State> {
	state: State = {
		hasError: false,
	};

	static getDerivedStateFromError() {
		// Update state so the next render will show the fallback UI.
		return { hasError: true };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		const { analytics, appName, fireEvent } = this.props;

		logException(error, 'Error caught in ErrorBoundary', {
			info: errorInfo,
			tags: {
				severity: 'high',
			},
		});

		if (fg('ptc-enable-ptc-common-analytics-refactor')) {
			fireEvent('operational.errorBoundary.triggered', {
				product: appName,
				boundary: 'app',
				error: error.toString(),
			});
		} else {
			analytics.pushOperationalEvent({
				source: AnalyticsEventSource.CLIENT_METRICS,
				actionSubject: 'errorBoundary',
				action: AnalyticsEventActions.TRIGGERED,
				attributes: {
					product: appName,
					boundary: 'app',
					error: error.toString(),
				},
				tags: ['error', 'error-boundary'],
			});
		}
	}

	render() {
		if (!this.state.hasError) {
			return this.props.children;
		}

		return <GenericErrorPage hasSupportLink />;
	}
}
